import { getHealthz, getReadyz } from '../api/endpoints.js';
import { useApi } from '../api/hooks.js';
import { Badge, Failure, Note, Panel, Raw, Screen } from '../ui/kit.js';

/**
 * What is actually wired behind this client.
 *
 * The two probes are live. The table below is NOT: it is transcribed from
 * deploy/README.md and deploy/ecs/api.json, because no endpoint publishes gateway
 * modes and this app does not invent data. Treat it as documentation that can go
 * stale, and check the task definition when it matters.
 */
const DOCUMENTED_DEV_MODES: { key: string; mode: string; consequence: string }[] = [
  { key: 'STRIPE_GATEWAY', mode: 'live', consequence: 'test-mode keys — subscribing works' },
  {
    key: 'DOCUSEAL_GATEWAY',
    mode: 'live',
    consequence: 'dev template; LiveDocuSealGateway has never run — the first signing is its first test',
  },
  { key: 'LIVENESS_GATEWAY', mode: 'live', consequence: 'real services; also selects the real attribution client' },
  { key: 'PUSH_PROVIDER', mode: 'fcm', consequence: 'dev Firebase project, on api and worker' },
  {
    key: 'SMS_PROVIDER',
    mode: 'twilio',
    consequence:
      'real OTPs and quiz texts — but the subaccount is a trial, so only numbers verified in the Twilio console receive them',
  },
  {
    key: 'EMAIL_PROVIDER',
    mode: 'ses',
    consequence: 'inside the SES sandbox — verification and email invites reach only addresses verified in the SES console',
  },
  { key: 'APPLE_GATEWAY', mode: 'fake', consequence: 'the stored key is base64 and the gateway wants PEM' },
  { key: 'SEARCH_PROVIDER', mode: 'stub', consequence: 'Hive has no sandbox and bills against real adult-content indexes' },
];

export function StatusScreen() {
  const healthz = useApi(getHealthz, [], { pollMs: 15_000 });
  const readyz = useApi(getReadyz, [], { pollMs: 15_000 });

  return (
    <Screen
      title="Environment status"
      lede="Two live probes, and an honest list of what is and is not wired behind them. This screen is why the demo does not need a developer mode."
    >
      <Panel title="probes">
        <p>
          <code>/healthz</code>{' '}
          {healthz.error !== null ? (
            <Badge tone="bad">down</Badge>
          ) : healthz.data !== null ? (
            <Badge tone="ok">{healthz.data.status}</Badge>
          ) : (
            <Badge>checking…</Badge>
          )}
        </p>
        {healthz.error !== null && <Failure error={healthz.error} />}

        <p style={{ marginTop: 12 }}>
          <code>/readyz</code>{' '}
          {readyz.error !== null ? (
            <Badge tone="bad">not ready</Badge>
          ) : readyz.data !== null ? (
            <Badge tone="ok">{readyz.data.status}</Badge>
          ) : (
            <Badge>checking…</Badge>
          )}
        </p>
        {readyz.error !== null && (
          <Failure
            error={readyz.error}
            hint={
              readyz.error.code === 'NOT_READY'
                ? 'The contract check failed: /readyz requires the three mandatory svc.* views to answer with the columns the readers select. Migration 0016 on the services side creates all four.'
                : 'Nothing answered on the other side of the proxy. Locally that means no api on API_ORIGIN (pnpm dev:api, PORT=3000); deployed it means Caddy is not proxying /v1 to the api yet (DEPLOY-DEV.md §7 item 8).'
            }
          />
        )}
      </Panel>

      <Panel title="provider modes — transcribed, not read">
        <Note>
          Nothing on the api publishes these, so the rows below are copied from{' '}
          <code>deploy/README.md</code> and <code>deploy/ecs/api.json</code>. They can go stale;
          the task definition is the truth.
        </Note>
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th>key</th>
                <th>dev</th>
                <th>what it means here</th>
              </tr>
            </thead>
            <tbody>
              {DOCUMENTED_DEV_MODES.map((entry) => (
                <tr key={entry.key}>
                  <td>
                    <code>{entry.key}</code>
                  </td>
                  <td>
                    <Badge tone={entry.mode === 'fake' || entry.mode === 'stub' ? 'warn' : 'ok'}>
                      {entry.mode}
                    </Badge>
                  </td>
                  <td>{entry.consequence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="known blockers">
        <ul>
          <li>
            <strong>Login.</strong> <code>SMS_PROVIDER=twilio</code> on the deployed api, so an
            OTP is really sent — but the subaccount is a trial, which delivers only to numbers
            verified in the Twilio console. An unverified number gets{' '}
            <code>SMS_DELIVERY_FAILED</code>, never a false success.
          </li>
          <li>
            <strong>Enrolment.</strong> A subject with no date of birth gets{' '}
            <code>DOB_REQUIRED</code>. It is write-once and decides self- versus
            guardian-signature on a legal record, so it is set deliberately on the profile screen
            and never inferred.
          </li>
          <li>
            <strong>Photo upload.</strong> The uploads bucket needs CORS allowing <code>PUT</code>{' '}
            from this origin.
          </li>
          <li>
            <strong>Photo protection.</strong> The image-worker transport is unwired, so{' '}
            <code>protected</code> stays <code>false</code>.
          </li>
          <li>
            <strong>Clusters.</strong> <code>FakeClusterClient</code> is still the default port.
          </li>
        </ul>
      </Panel>

      <Raw label="probe responses" value={{ healthz: healthz.data, readyz: readyz.data }} />
    </Screen>
  );
}
